import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';

import {
  collection, collectionData, CollectionReference, collectionSnapshots, deleteDoc, doc, docData, DocumentData, endBefore, Firestore,
  FirestoreDataConverter, getCountFromServer, limit, orderBy, query, QueryConstraint, serverTimestamp, setDoc, startAfter, updateDoc,
  where
} from '@angular/fire/firestore';

import { BaseModel } from '../../global/models';
import { DEFAULT_DATE_PROPERTIES, DEFAULT_QUERY_LIMIT, defaultFirestoreConverter } from './firestore.helper';
import { FirestoreBaseOptions, PaginationConstraints, PaginationResult, QueryConstraints, QueryFilterTuple, QueryOptions, QueryOrderEntry, QueryWhereInput, WithFieldValue } from './firestore.models';

/**
 * Abstract base class for Firestore database services.
 * Provides an abstraction layer for managing collections, data typing, and
 * automatic date conversion (Firestore Timestamps to native JS Dates).
 * @template Model - The data model type, which must extend `BaseModel`.
 * @example
 * // Implementation in a domain service (UserDbService)
 * export class UserDbService extends FirestoreBase<User> {
 *   constructor(firestore: Firestore) {
 *     super('users', firestore, {
 *       dateProperties: ['lastLogin', 'birthDate'],
 *       allowGetAll: true,
 *       // Optional: Custom Firestore converter
 *       firestoreConverter: userCustomConverter
 *     });
 *   }
 * }
 * @example
 * // Example of a custom FirestoreDataConverter
 * const userCustomConverter: FirestoreDataConverter<User> = {
 *   toFirestore: (user: WithFieldValue<User>): DocumentData => {
 *     const { id, ...data } = user; // Exclude ID from the document body
 *     return data as DocumentData;
 *   },
 *   fromFirestore: (snapshot: QueryDocumentSnapshot, options: SnapshotOptions): User => {
 *     const data = snapshot.data(options)!;
 *     return {
 *       ...data,
 *       id: snapshot.id,
 *       fullName: `${data['firstName']} ${data['lastName']}`
 *     } as User;
 *   }
 * };
 */
export abstract class FirestoreBase<Model extends BaseModel> {
  private readonly injector = inject(EnvironmentInjector);
  protected collection: CollectionReference<Model>;
  protected dateProperties: string[];
  protected allowGetAll: boolean;

  constructor(
    protected collectionName: string,
    protected firestore: Firestore,
    protected options: FirestoreBaseOptions<Model> = {} // Optional options object (default)
  ) {
    const combinedDates = [
      ...DEFAULT_DATE_PROPERTIES,
      ...(options.dateProperties ?? [])
    ];
    this.dateProperties = Array.from(new Set(combinedDates)); // Remove duplicates
    this.allowGetAll = options.allowGetAll ?? false;

    if (this.firestore) {
      // Determine converter: prefer provided one in options, otherwise default
      const converter: FirestoreDataConverter<Model> = options.firestoreConverter
        ? options.firestoreConverter as FirestoreDataConverter<Model>
        : defaultFirestoreConverter<Model>(this.dateProperties);

      // Apply converter directly to the collection reference
      this.collection = collection(this.firestore, this.collectionName)
        .withConverter(converter);
    }
  }

  /**
   * Executes a function within an Angular injection context.
   * Prevents the warning: "Calling Firebase APIs outside of an Injection context
   * may destabilize your application leading to subtle change-detection and hydration bugs."
   */
  protected run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  // CRUD methods

  /**
   * Retrieves a single document as an Observable for real-time updates.
   * Supports subcollections via options.
   * @param id - The unique Firestore document identifier.
   * @param [options] - Optional configuration for subpath and custom converters.
   * @returns An Observable emitting the document data.
   */
  get$<T extends BaseModel = Model>(
    id: string,
    options?: QueryOptions<T>
  ): Observable<T> {
    return this.run(() => {
      const { subPath, converter } = (options || {}) as QueryOptions<T>;
      let collectionRef: CollectionReference<any> = this.collection;

      if (subPath) {
        const subCol = collection(this.collection, subPath);
        collectionRef = subCol.withConverter(converter || defaultFirestoreConverter<T>());
      }

      const docRef = doc(collectionRef, id);
      return docData(docRef, { idField: 'id' }) as Observable<T>;
    });
  }

  /**
   * Retrieves a single document as a Promise (one-shot fetch).
   * Supports subcollections via options.
   * @param id - The unique Firestore document identifier.
   * @param [options] - Optional configuration for subpath and custom converters.
   * @returns A Promise that resolves with the document data or undefined.
   */
  get<T extends BaseModel = Model>(
    id: string,
    options?: QueryOptions<T>
  ): Promise<T> {
    return firstValueFrom(this.get$<T>(id, options));
  }

  /**
   * Retrieves all documents from the collection or a specified subcollection without filters.
   * Consider using {@link getWhere$} for more advanced filtering and performance.
   * @template T - The model type, defaults to the class Model. Must extend BaseModel.
   * @param {QueryOptions<T>} [options] - Configuration for subcollection queries.
   * @throws {Error} If 'allowGetAll' is disabled for this collection.
   * @returns {Observable<T[]>} An Observable emitting the array of documents with their IDs.
   */
  getAll$<T extends BaseModel = Model>(
    options?: QueryOptions<T>
  ): Observable<T[]> {
    if (!this.allowGetAll) {
      const err = new Error(`${this.collectionName} not allowed to invoke getAll$()/getAll()`);
      console.error(err);
      throw err;
    }

    return this.run(() => {
      const { subPath, converter } = (options || {}) as QueryOptions<T>;

      // 1. Determine the base reference (main or subcollection)
      let collectionRef: CollectionReference<any> = this.collection;

      if (subPath) {
        const subCol = collection(this.collection, subPath);
        // Apply provided converter or the default for subcollections
        collectionRef = subCol.withConverter(converter || defaultFirestoreConverter<T>());
      }

      // 2. Execute the query without additional constraints
      const _query = query(collectionRef);

      return collectionData<T>(_query, { idField: 'id' });
    });
  }

  /**
   * Retrieves all documents from the collection or a specified subcollection as a Promise.
   * Derives from getAll$() and inherits the 'allowGetAll' validation and subcollection logic.
   * @template T - The model type, defaults to Model. Must extend BaseModel.
   * @param {QueryOptions<T>} [options] - Configuration for subcollection queries (subPath, converter).
   * @throws {Error} If the collection does not have 'allowGetAll' enabled.
   * @returns {Promise<T[]>} A Promise that resolves with an array of all documents.
   */
  getAll<T extends BaseModel = Model>(
    options?: QueryOptions<T>
  ): Promise<T[]> {
    // firstValueFrom subscribes, takes the first value from getAll$ and closes the stream.
    return firstValueFrom(this.getAll$<T>(options));
  }

  /**
   * Retrieves a list of documents from the collection or a specified subcollection.
   * @template T - The model type, defaults to the class Model. Must extend BaseModel.
   * @example
   * // Equality shortcuts
   * getWhere$({ where: { status: 'active', enabled: true } })
   * @example
   * // Advanced operators using tuples
   * getWhere$({ where: ['price', '>=', 100] })
   * @example
   * // Mixing syntaxes and multiple sorting
   * getWhere$({
   *   where: [ { category: 'electronics' }, ['price', '<', 500] ],
   *   orderBy: [ { price: 'desc' }, { createdAt: 'asc' } ]
   * })
   * @param {QueryConstraints} [constraints] - Configuration for filtering, sorting, and limits.
   * @param {QueryOptions<T>} [options] - Configuration for subcollection queries.
   * @returns {Observable<T[]>} An observable that emits the array of documents with their IDs.
   */
  getWhere$<T extends BaseModel = Model>(
    constraints: QueryConstraints = {},
    options?: QueryOptions<T>
  ): Observable<T[]> {
    return this.run(() => {
      const _constraints: QueryConstraint[] = [];
      const { where: whereOpt, orderBy: orderOpt, limit: limitOpt } = constraints;
      const { subPath, converter } = (options || {}) as QueryOptions<T>;

      // 0. Reference setup
      let collectionRef: CollectionReference<any> = this.collection;
      if (subPath) {
        const subCol = collection(this.collection, subPath);
        collectionRef = subCol.withConverter(converter || defaultFirestoreConverter<T>());
      }

      // 1. Process WHERE filters using the normalizer
      if (whereOpt) {
        _constraints.push(...this.normalizeWhere(whereOpt));
      }

      // 2. Process ORDER BY using the normalizer
      if (orderOpt) {
        _constraints.push(...this.normalizeOrderBy(orderOpt));
      }

      // 3. Process LIMIT
      if (limitOpt === undefined) {
        console.warn(`[Firestore] getWhere$ called without limit for "${this.collectionName}". Defaulting to ${DEFAULT_QUERY_LIMIT}.`);
      }
      _constraints.push(limit(limitOpt ?? DEFAULT_QUERY_LIMIT));

      const _query = query(collectionRef, ..._constraints);
      return collectionData<T>(_query, { idField: 'id' });
    });
  }

  /**
   * Retrieves a list of documents as a Promise (one-shot fetch).
   * It derives from getWhere$ to ensure consistent filtering and mapping logic.
   * @template T - The model type, defaults to Model. Must extend BaseModel.
   * @param [constraints] - Configuration object for filtering, sorting, and limits.
   * @param [options] - Configuration for subcollection queries.
   * @returns A Promise that resolves with an array of documents.
   */
  getWhere<T extends BaseModel = Model>(
    constraints: QueryConstraints = {},
    options?: QueryOptions<T>
  ): Promise<T[]> {
    return firstValueFrom(this.getWhere$<T>(constraints, options));
  }

  /**
   * Retrieves a single document based on provided filters.
   * Useful for finding a unique record by a specific field (e.g., slug, email, code).
   * @template T - The model type, defaults to the class Model. Must extend BaseModel.
   * @param [options] - Configuration object for filtering and sorting.
   * @param [options] - Configuration for subcollection queries.
   * @returns An Observable emitting the found document or undefined if no results.
   */
  getOneWhere$<T extends BaseModel = Model>(
    constraints: QueryConstraints = {},
    options?: QueryOptions<T>
  ): Observable<T | undefined> {
    // We force the limit to 1 for performance and take only the first result of the array
    return this.getWhere$<T>({ ...constraints, limit: 1 }, options).pipe(
      map(results => results[0])
    );
  }

  /**
   * Retrieves a single document based on filters as a Promise.
   * Derives from getOneWhere$ to maintain the limit: 1 optimization.
   * @template T - The model type, defaults to Model. Must extend BaseModel.
   * @param [constraints] - Configuration object for filtering and sorting.
   * @param [options] - Configuration for subcollection queries.
   * @returns A Promise that resolves with the found document or undefined.
   */
  getOneWhere<T extends BaseModel = Model>(
    constraints: QueryConstraints = {},
    options?: QueryOptions<T>
  ): Promise<T | undefined> {
    return firstValueFrom(this.getOneWhere$<T>(constraints, options));
  }

  /**
   * Retrieves a paginated list of documents using Firestore Snapshots as cursors.
   * This method is more efficient than offset-based pagination and works with real-time updates.
   * @template T - The model type, defaults to the class Model. Must extend BaseModel.
   * @param {PaginationConstraints} [constraints] - Includes filters, sorting, and cursor snapshots.
   * @param {QueryOptions<T>} [options] - Configuration for subcollection queries.
   * @example
   * // 1. Initial page fetch
   * this.paginate$({
   *   limit: 10,
   *   orderBy: { name: 'asc' },
   *   where: { status: 'active' }
   * }).subscribe(res => this.currentPage = res);
   * @example
   * // 2. Navigate to NEXT page
   * if (this.currentPage.navigation.hasNext) {
   * this.paginate$({
   *   limit: 10,
   *   orderBy: { name: 'asc' },
   *   startAfter: this.currentPage.cursors.last
   * }).subscribe(res => this.currentPage = res);
   * }
   * @example
   * // 3. Navigate to PREVIOUS page
   * if (this.currentPage.navigation.hasPrev) {
   * this.paginate$({
   *   limit: 10,
   *   orderBy: { name: 'asc' },
   *   endBefore: this.currentPage.cursors.first
   * }).subscribe(res => this.currentPage = res);
   * }
   * @example
   * // 4. Paginating a subcollection
   * this.paginate$({ limit: 5 }, { subPath: 'groups/admin/members' });
   * @returns {Observable<PaginationResult<T>>} Enriched object with items, cursors, and navigation flags.
   */
  paginate$<T extends BaseModel = Model>(
    constraints: PaginationConstraints = {},
    options?: QueryOptions<T>
  ): Observable<PaginationResult<T>> {
    return this.run(() => {
      const _constraints: QueryConstraint[] = [];
      const {
        where: whereOpt,
        orderBy: orderOpt,
        limit: limitOpt = DEFAULT_QUERY_LIMIT,
        startAfter: startAfterOpt,
        endBefore: endBeforeOpt
      } = constraints;

      const { subPath, converter } = (options || {}) as QueryOptions<T>;

      // 0. Reference setup
      let collectionRef: CollectionReference<any> = this.collection;
      if (subPath) {
        const subCol = collection(this.collection, subPath);
        collectionRef = subCol.withConverter(converter || defaultFirestoreConverter<T>());
      }

      // 1. Core constraints (Filters and Ordering)
      if (whereOpt) _constraints.push(...this.normalizeWhere(whereOpt));
      if (orderOpt) _constraints.push(...this.normalizeOrderBy(orderOpt));

      // 2. Pagination Cursors
      if (startAfterOpt) _constraints.push(startAfter(startAfterOpt));
      if (endBeforeOpt) _constraints.push(endBefore(endBeforeOpt));

      // 3. Request limit + 1 to determine hasNext without a separate count
      _constraints.push(limit(limitOpt + 1));

      const _query = query(collectionRef, ..._constraints);

      // collectionSnapshots provides the raw metadata needed for cursors
      return collectionSnapshots(_query).pipe(
        map(actions => {
          // Map snapshots to data using the converter logic already applied to collectionRef
          const items = actions.map(a => {
            const data = a.data() as T;
            return { ...data, id: a.id };
          });

          const hasMore = items.length > limitOpt;

          // If we have more than the limit, remove the extra item used for the check
          if (hasMore) {
            items.pop();
          }

          const first = actions.length > 0 ? actions[0] : null;
          const last = hasMore ? actions[items.length - 1] : (actions.length > 0 ? actions[actions.length - 1] : null);

          return {
            items,
            cursors: {
              first,
              last
            },
            navigation: {
              hasNext: hasMore,
              // Simple logic: if we provided a cursor, we assume there is a previous page
              hasPrev: !!startAfterOpt || !!endBeforeOpt
            }
          };
        })
      );
    });
  }

  /**
   * Retrieves a paginated list of documents as a Promise (one-shot fetch).
   * Useful for scenarios where real-time updates are not required (e.g., exports or static reports).
   * @template T - The model type, defaults to the class Model. Must extend BaseModel.
   * @param {PaginationConstraints} [constraints] - Includes filters, sorting, and cursor snapshots.
   * @param {QueryOptions<T>} [options] - Configuration for subcollection queries.
   * @example
   * // 1. Initial page fetch
   * const result = await this.userDb.paginate({
   *   limit: 10,
   *   orderBy: { createdAt: 'desc' }
   * });
   * @example
   * // 2. Navigate to NEXT page safely
   * if (result.navigation.hasNext) {
   *   const nextPage = await this.userDb.paginate({
   *     limit: 10,
   *     orderBy: { createdAt: 'desc' },
   *     startAfter: result.cursors.last
   *   });
   * }
   * @example
   * // 3. Paginating a subcollection as a Promise
   * const subItems = await this.userDb.paginate(
   *   { limit: 20 },
   *   { subPath: 'orders/2024/items' }
   * );
   * @returns {Promise<PaginationResult<T>>} A Promise resolving to the enriched pagination object.
   */
  paginate<T extends BaseModel = Model>(
    constraints: PaginationConstraints = {},
    options?: QueryOptions<T>
  ): Promise<PaginationResult<T>> {
    return firstValueFrom(this.paginate$<T>(constraints, options));
  }

  /**
   * Counts documents matching the given constraints as a Promise.
   * Uses Firestore's server-side `getCountFromServer` for efficient counting without downloading documents.
   * @template T - The model type, defaults to Model. Must extend BaseModel.
   * @param {QueryConstraints} [constraints] - Configuration for filtering (where). Limit and orderBy are ignored for counting.
   * @param {QueryOptions<T>} [options] - Configuration for subcollection queries.
   * @example
   * const total = await this.countDocs({ where: { status: 'active' } });
   * @returns {Promise<number>} A Promise resolving to the document count.
   */
  async countDocs<T extends BaseModel = Model>(
    constraints: QueryConstraints = {},
    options?: QueryOptions<T>
  ): Promise<number> {
    return this.run(async () => {
      const _constraints: QueryConstraint[] = [];
      const { where: whereOpt } = constraints;
      const { subPath, converter } = (options || {}) as QueryOptions<T>;

      let collectionRef: CollectionReference<any> = this.collection;
      if (subPath) {
        const subCol = collection(this.collection, subPath);
        collectionRef = subCol.withConverter(converter || defaultFirestoreConverter<T>());
      }

      if (whereOpt) {
        _constraints.push(...this.normalizeWhere(whereOpt));
      }

      const _query = query(collectionRef, ..._constraints);
      const snapshot = await getCountFromServer(_query);
      return snapshot.data().count;
    });
  }

  /**
   * Creates or updates a document using a merge strategy.
   * Supports subcollections via options.
   * @template T - The model type, defaults to Model.
   * @param data - The document data to save.
   * @param [options] - Optional configuration for subpath.
   * @returns A Promise resolving to the document ID.
   */
  async upsert<T extends BaseModel = Model>(
    data: WithFieldValue<T>,
    options?: QueryOptions<T>
  ): Promise<string> {
    return this.run(async () => {
      const { subPath } = (options || {}) as QueryOptions<T>;
      const id = (data.id || this.createId()) as string;
      const isNew = !data.id;

      // Resolve reference (Main or Sub)
      const baseRef = subPath ? collection(this.collection, subPath) : this.collection;
      const docRef = doc(baseRef, id);

      const payload: DocumentData = {
        ...(data as DocumentData),
        id,
        updatedAt: serverTimestamp(),
        ...(isNew ? { createdAt: serverTimestamp() } : {})
      };

      await setDoc(docRef, payload, { merge: true });
      return id as string;
    });
  }

  /**
   * Updates an existing document. Supports subcollections.
   * @param id - The unique identifier of the document.
   * @param data - The partial data to update (supports dot notation).
   * @param [options] - Optional configuration for subpath.
   * @returns A Promise that resolves when the update is complete.
   */
  update<T extends BaseModel = Model>(
    id: string,
    data: WithFieldValue<T> | { [key: string]: any; },
    options?: QueryOptions<T>
  ): Promise<void> {
    return this.run(() => {
      const { subPath } = (options || {}) as QueryOptions<T>;
      const baseRef = subPath ? collection(this.collection, subPath) : this.collection;
      const docRef = doc(baseRef, id);

      const payload: DocumentData = {
        ...(data as DocumentData),
        updatedAt: serverTimestamp(),
      };
      return updateDoc(docRef, payload);
    });
  }

  /**
   * Creates a new document. Supports subcollections.
   * @param data - The document data to create.
   * @param [options] - Optional configuration for subpath.
   * @returns A Promise resolving to the new document ID.
   */
  async create<T extends BaseModel = Model>(
    data: T | Partial<T>,
    options?: QueryOptions<T>
  ): Promise<string> {
    return this.run(async () => {
      const { subPath } = (options || {}) as QueryOptions<T>;
      const id = data.id || this.createId();

      const baseRef = subPath ? collection(this.collection, subPath) : this.collection;
      const docRef = doc(baseRef, id);

      const payload: any = {
        ...data,
        id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, payload);
      return id;
    });
  }

  /**
   * Permanently removes a document. Supports subcollections.
   * @param id - The unique identifier of the document to delete.
   * @param [options] - Optional configuration for subpath.
   * @returns A Promise that resolves when deleted.
   */
  delete<T extends BaseModel = Model>(
    id: string,
    options?: QueryOptions<T>
  ): Promise<void> {
    return this.run(() => {
      const { subPath } = (options || {}) as QueryOptions<T>;
      const baseRef = subPath ? collection(this.collection, subPath) : this.collection;
      const docRef = doc(baseRef, id);

      return deleteDoc(docRef);
    });
  }

  /**
   * Generates a unique Firestore document ID without performing any write operation.
   * Useful when you need an ID before creating a document (e.g., for local state).
   * @returns A 20-character unique string ID.
   */
  createId(): string {
    // Calling doc() on a collection reference without an ID generates a new one.
    return this.run(() => doc(this.collection).id);
  }

  /**
   * Normalizes various 'where' syntaxes into Firestore QueryConstraints.
   */
  private normalizeWhere(input: QueryWhereInput): QueryConstraint[] {
    const filters: QueryConstraint[] = [];

    // Convert single input to array for unified processing
    const items = Array.isArray(input) && !this.isTuple(input) ? input : [input];

    items.forEach(item => {
      if (this.isTuple(item)) {
        // Case: ['field', 'op', 'value']
        const [field, op, value] = item as QueryFilterTuple;
        filters.push(where(field, op, value));
      } else {
        // Case: { field: value, anotherField: value }
        Object.keys(item).forEach(key => {
          filters.push(where(key, '==', (item as any)[key]));
        });
      }
    });

    return filters;
  }

  /**
   * Normalizes 'orderBy' object or array into Firestore QueryConstraints.
   */
  private normalizeOrderBy(input: QueryOrderEntry | QueryOrderEntry[]): QueryConstraint[] {
    const items = Array.isArray(input) ? input : [input];
    return items.map(item => {
      const field = Object.keys(item)[0];
      const direction = item[field] || 'asc';
      return orderBy(field, direction);
    });
  }

  /**
   * Helper to distinguish between a Tuple ['a', '==', 'b'] and a regular Array
   */
  private isTuple(item: any): boolean {
    return Array.isArray(item) && typeof item[0] === 'string' && item.length === 3;
  }
}
